import clsx from 'clsx';
import { Form, Formik } from 'formik';
import React from 'react';
import * as Yup from 'yup';

import { PrimaryButton } from '@components/Button/variants';
import SwitchField from '@components/Form/SwitchField';
import TextField from '@components/Form/TextField';
import FormErrors from '@components/FormErrors';
import { useTeamUpdate } from '@hooks';
import { Theme } from '@interfaces/global';
import { Team } from '@utils/backend-api/teams';

import SettingHeaderSeperator from '../SettingHeaderSeperator';

const EMAIL_DOMAIN_REGEX = /^((?:https?:\/\/)?[^./]+(?:\.[^./]+)+(?:\/.*)?)$/;

const validationSchema = () =>
  Yup.object({
    emailDomain: Yup.string().matches(EMAIL_DOMAIN_REGEX, 'Please enter a valid domain').nullable(),
  });

export default function EmailDomainForm({ team }: { team: Team }) {
  const { updateTeamAsync, updateTeamData } = useTeamUpdate();

  const initialValues = {
    emailDomain: team?.email_domain?.replace('@', '0') || '',
    addByDomain: !!team?.email_domain,
  };

  return (
    <div className="mb-48" data-test="team-settings-domain">
      <Formik
        initialValues={initialValues}
        onSubmit={async (values) =>
          updateTeamAsync({
            email_domain: values.emailDomain,
          })
        }
        validationSchema={validationSchema}
        enableReinitialize={true}
      >
        {({ isSubmitting, values }) => (
          <Form>
            <SettingHeaderSeperator sectionName="Add by corporate domain" />
            <div className="flex items-start">
              <div
                className={clsx('font-suisse-intl text-description-lg', {
                  'mb-24': values.addByDomain,
                })}
              >
                Allow anyone on my team that signs in with their corporate email to subscribe to The
                Information on my account. Each additional team member will be charged at an annual
                cost of $399 per year.
              </div>
              <SwitchField colorTheme={Theme.Light} name="addByDomain" />
            </div>
            {values.addByDomain && (
              <>
                <TextField
                  className="mb-24"
                  name="emailDomain"
                  placeholder="Enter a corporate domain (e.g., example.com)"
                />
                {!!updateTeamData?.errors && <FormErrors errors={updateTeamData.errors} />}
                <PrimaryButton
                  as="button"
                  className="mt-24"
                  type="submit"
                  disabled={isSubmitting}
                  fullWidth
                >
                  Save
                </PrimaryButton>
              </>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
}
